/**
 * SidebarSection — top-level (level-1) collapsible section with icon.
 * Active section highlights icon + label in brand green. Chevron rotates on open.
 */
import { ICON_BY_NAME, Chevron, Layers } from "./icons";
import { SidebarSubsection } from "./SidebarSubsection";
import type { SidebarNode } from "../../lib/sidebar-tree";

export interface SidebarSectionProps {
	node: SidebarNode;
	open: boolean;
	onToggle: () => void;
	activeHref: string;
	collapsed: boolean; // true = whole sidebar collapsed (show only icon)
	openPaths: Set<string>;
	toggleOpen: (path: string) => void;
	hasActive: boolean;
}

export function SidebarSection({
	node,
	open,
	onToggle,
	activeHref,
	collapsed,
	openPaths,
	toggleOpen,
	hasActive,
}: SidebarSectionProps) {
	const Icon = ICON_BY_NAME[node.icon ?? ""] ?? Layers;

	if (collapsed) {
		return (
			<li role="none" className="flex flex-col items-center">
				<button
					type="button"
					role="treeitem"
					aria-expanded={false}
					aria-level={1}
					title={node.label}
					onClick={onToggle}
					className={
						hasActive
							? "w-10 h-10 flex items-center justify-center rounded-md text-green-700 bg-green-50"
							: "w-10 h-10 flex items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-50 hover:text-zinc-700"
					}
				>
					<Icon className="w-4 h-4" />
				</button>
			</li>
		);
	}

	return (
		<li role="none">
			<button
				type="button"
				role="treeitem"
				aria-expanded={open}
				aria-level={1}
				onClick={onToggle}
				className={
					hasActive
						? "w-full flex items-center gap-2 px-3 py-2 text-[11px] font-semibold tracking-wider uppercase text-green-700 bg-green-50 rounded-md"
						: "w-full flex items-center gap-2 px-3 py-2 text-[11px] font-semibold tracking-wider uppercase text-zinc-600 hover:bg-zinc-50 rounded-md"
				}
			>
				<Icon
					className={
						open || hasActive
							? "w-4 h-4 text-green-600"
							: "w-4 h-4 text-zinc-500"
					}
				/>
				<span className="flex-1 text-left truncate">{node.label}</span>
				<Chevron
					className={
						open
							? "w-3 h-3 text-zinc-500 transition-transform duration-150 rotate-90"
							: "w-3 h-3 text-zinc-400 transition-transform duration-150"
					}
				/>
			</button>

			{open ? (
				<ul role="group" className="mt-1 mb-2 flex flex-col">
					{node.children?.map((child) => {
						const path = `${node.label}/${child.label}`;
						return (
							<SidebarSubsection
								key={path}
								node={child}
								depth={1}
								open={openPaths.has(path)}
								onToggle={() => toggleOpen(path)}
								activeHref={activeHref}
								path={path}
								openPaths={openPaths}
								toggleOpen={toggleOpen}
							/>
						);
					})}
				</ul>
			) : null}
		</li>
	);
}
